import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function BlogPostsMasonry() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="break-inside-avoid mb-6">
            <div className="h-64 w-full rounded-xl bg-muted/20 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4">Si è verificato un errore nel caricamento degli articoli.</p>
        <Button onClick={refetch} variant="outline">Riprova</Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        Nessun articolo trovato.
      </div>;
  }
  return <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
      {posts.map((post, i) => {
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Tech Insight";
      const imageSrc = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/technical_server_rack_blue.png";
      return <div key={post.id} data-index={i} className="break-inside-avoid mb-6">
            <Card className="bg-card/50 border-border/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300 overflow-hidden group h-full flex flex-col">
              <div className="relative h-48 overflow-hidden">
                <img src={imageSrc} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[0.3] group-hover:grayscale-0" />
                <div className="absolute top-4 left-4">
                  <Badge variant="secondary" className="bg-background/80 backdrop-blur-md text-primary border-primary/20 shadow-sm">
                    #{category}
                  </Badge>
                </div>
              </div>
              
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                  <span className="flex items-center gap-1">
                    <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                    {post.date ? new Date(post.date).toLocaleDateString('it-IT') : 'Recente'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    5 min lettura
                  </span>
                </div>
                <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                  <Link to={post.link || "#"}>
                    {post.title}
                  </Link>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-grow">
                <div className="text-muted-foreground text-sm line-clamp-3" dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
              </CardContent>
              
              <CardFooter className="pt-0 mt-auto">
                <Link to={post.link || "#"} className="group/link flex items-center text-sm font-medium text-primary">
                  <span className="relative">
                    Leggi di più
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover/link:w-full"></span>
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </CardFooter>
            </Card>
          </div>;
    })}
    </div>;
}
export default function InsightTecnici() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="insight-tecnici" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/circuit_pattern_overlay.png')] opacity-[0.03] bg-repeat" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
              Blog & News
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Insight <span className="text-primary">Tecnici</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Approfondimenti su infrastrutture IT, cybersecurity e gestione dei dispositivi aziendali.
              Analisi tecniche per supportare le decisioni del tuo business.
            </p>
          </div>
          
          <div className="shrink-0">
            <Link to="/BlogPage">
              <Button variant="outline" className="border-primary/20 hover:bg-primary/10 hover:text-primary">
                Tutti gli articoli
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }}>
          <BlogPostsMasonry />
        </PostsProvider>
      </div>
    </section>;
}